/**
 * useTimepointExamProblems — 추이 차트의 (시점=KST날짜, 개념 cid) → 그 시점 시험의 문제 목록(정오 포함).
 *
 * 기본 흐름(구 화면, 모두 기존 API 재사용):
 *   1) GET /api/exams/summary?student_id=  → stamps[].exams[] {stamp_id, exam_id, updated_at}. 학생별 1회 캐시.
 *   2) kstDateOf(updated_at) === dateKST 인 시험 필터 → (stamp_id, exam_id)[]  (0건=기록없음, 복수=합침)
 *   3) 각 시험 GET /api/exams/{stamp}/{exam}/result → data.problem_results [{problem_id, result}]  (문제+정오 동시)
 *   4) GET /api/mathcepts/temp/by-prob-ids → 문제별 concept_ids → 클릭 개념 포함 여부로 강조/그외 분리
 *
 * 학습 v2(includeV2Assignments=true)는 위 결과에 마감된 v2 배정을 더한다:
 *   - GET /api/v2/exam-papers/students/{id}/assignments?is_closed=true 를 100개씩 끝까지 조회·학생별 캐시
 *   - kstDateOf(closed_at || assigned_at) 기준으로 같은 날의 items[]를 합침
 *   - 문제→개념은 v2 problem-search를 사용(구 시험 문제도 같은 문제 번호로 조회 가능)
 *
 * 날짜 매칭 기준 = updated_at(마감·재채점 시각). 추이 차트 X축(availableTimelineDates)·슬라이더 범위가
 * 구 시험은 kstDateOf(exam.updated_at), v2 배정은 엔진과 같은 kstDateOf(closed_at || assigned_at)를 써야
 * 시점↔시험이 일치한다.
 * (created_at=응시 생성시각으로 매칭하면 저녁 응시·익일 마감 시험이 하루 어긋나 오매칭/미매칭된다.)
 */
import { useCallback, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchAllStudentAssignments } from '../components/ExamPapersV2/examPaperApi';
import { fetchProblemItemsV2 } from '../components/ExamPapersV2/problemBodyV2';
import { fetchWithTokenRefresh } from './apiUtils';
import { kstDateOf } from './aitrackerTimelineCache';

const BY_PROB_IDS_CHUNK = 100; // by-prob-ids 대용량 서버오류 방어(reportApi 등과 같은 관례)

export default function useTimepointExamProblems(studentId, { includeV2Assignments = false } = {}) {
  const { getAccessToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const summaryRef = useRef(null); // { studentId, exams:[{stamp_id, exam_id, updated_at}] } — 학생별 캐시
  const assignmentsRef = useRef(null); // { studentId, assignments:[...] } — 학습 v2에서만 학생별 캐시

  const load = useCallback(async (dateKST, conceptCid) => {
    if (!studentId || !dateKST) return null;
    setIsLoading(true);
    setError(null);
    try {
      // 1) 시험 요약(1회 캐시)
      if (!summaryRef.current || summaryRef.current.studentId !== String(studentId)) {
        try {
          const res = await fetchWithTokenRefresh(`/api/exams/summary?student_id=${studentId}`, {}, getAccessToken);
          const stamps = res?.data?.stamps || [];
          const exams = [];
          stamps.forEach((st) => (st.exams || []).forEach((ex) => exams.push({
            stamp_id: st.stamp_id,
            exam_id: ex.exam_id,
            updated_at: ex.updated_at,
          })));
          summaryRef.current = { studentId: String(studentId), exams };
        } catch (e) {
          // 이 구 API는 학생은 있지만 구 시험이 0건이어도 404를 준다. 학습 v2에서는 곧이어 부르는
          // v2 배정 API가 학생 존재·권한을 다시 검증하므로, 그 경우에만 구 시험 0건으로 이어 간다.
          if (!includeV2Assignments || e?.httpStatus !== 404) throw e;
          summaryRef.current = { studentId: String(studentId), exams: [] };
        }
      }

      // 학습 v2만 마감 배정을 전부 캐시한다. 다른 공유 화면은 이 API를 호출하지 않는다.
      if (includeV2Assignments
        && (!assignmentsRef.current || assignmentsRef.current.studentId !== String(studentId))) {
        const assignments = await fetchAllStudentAssignments(
          studentId,
          { isClosed: true },
          getAccessToken,
        );
        assignmentsRef.current = { studentId: String(studentId), assignments };
      }

      // 2) 클릭 날짜(KST)와 일치하는 구 시험 + v2 배정.
      // v2는 채점값이 하나도 없으면 엔진/Genetics에도 반영되지 않으므로 여기서도 시험으로 세지 않는다.
      const matchedExams = summaryRef.current.exams.filter((e) => kstDateOf(e.updated_at) === dateKST);
      const matchedAssignments = includeV2Assignments
        ? (assignmentsRef.current?.assignments || []).filter((a) => {
          const items = Array.isArray(a?.items) ? a.items : [];
          const hasGradedItem = items.some((it) => it?.result === true || it?.result === false);
          return a?.is_closed !== false
            && hasGradedItem
            && kstDateOf(a?.closed_at || a?.assigned_at) === dateKST;
        })
        : [];
      if (matchedExams.length === 0 && matchedAssignments.length === 0) {
        return { matched: false, problems: [], conceptC: [], other: [], totals: { n: 0, correct: 0 } };
      }

      // 3) 시험별 결과(문항+정오) → 병합(problem_id 중복 제거).
      // exam_id와 assignment_id는 서로 다른 번호 공간이므로 날짜별 목록을 따로 찾은 뒤 합친다.
      const perExam = await Promise.all(matchedExams.map((e) => fetchWithTokenRefresh(
        `/api/exams/${e.stamp_id}/${e.exam_id}/result`, {}, getAccessToken,
      ).then((r) => r?.data?.problem_results || []).catch(() => [])));
      const perAssignment = matchedAssignments.map((a) => (Array.isArray(a?.items) ? a.items : []));

      const seen = new Set();
      const records = [];
      [...perExam, ...perAssignment].flat().forEach((pr) => {
        const pid = pr?.problem_id;
        if (pid == null || seen.has(String(pid))) return;
        seen.add(String(pid));
        records.push({ problem_id: pid, result: pr.result });
      });
      if (records.length === 0) {
        return { matched: true, problems: [], conceptC: [], other: [], totals: { n: 0, correct: 0 } };
      }

      // 4) 문제→개념 매핑(best-effort: 실패 시 전부 '그 외'로 폴백)
      const ids = records.map((r) => r.problem_id);
      const conceptByPid = new Map();
      try {
        if (includeV2Assignments) {
          const items = await fetchProblemItemsV2(ids, getAccessToken);
          Object.values(items || {}).forEach((it) => {
            const pid = it?.problem_id ?? it?.id;
            if (pid != null) conceptByPid.set(String(pid), (it?.concept_ids || []).map(String));
          });
        } else {
          const chunks = [];
          for (let i = 0; i < ids.length; i += BY_PROB_IDS_CHUNK) chunks.push(ids.slice(i, i + BY_PROB_IDS_CHUNK));
          const flat = (await Promise.all(chunks.map((c) => fetchWithTokenRefresh(
            `/api/mathcepts/temp/by-prob-ids?problem_ids=${c.join(',')}`, {}, getAccessToken,
          ).then((r) => r?.data || []).catch(() => [])))).flat();
          flat.forEach((it) => conceptByPid.set(String(it.problem_id), (it.concept_ids || []).map(String)));
        }
      } catch { /* 개념 분류 생략 → 전부 그 외 */ }

      const cidStr = String(conceptCid);
      const conceptC = [];
      const other = [];
      records.forEach((r) => {
        const cids = conceptByPid.get(String(r.problem_id)) || [];
        (cids.includes(cidStr) ? conceptC : other).push(r);
      });
      const correct = records.filter((r) => r.result === true).length;
      return { matched: true, problems: records, conceptC, other, totals: { n: records.length, correct } };
    } catch (e) {
      setError(e?.message || '문제를 불러오지 못했습니다.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [studentId, getAccessToken, includeV2Assignments]);

  return { load, isLoading, error };
}
